/**
 * PrivateRoute
 * Wraps any route element that requires authentication.
 * Redirects to / (LoginPage) if access_token or retailUserDetails is missing
 * from localStorage.
 *
 * Uses an explicit allowlist of EXACT public paths rather than substring
 * matching, so adding a protected route whose path happens to contain a
 * public-sounding word never accidentally bypasses the guard.
 *
 * Usage in App.js / pathElements.js:
 *   element={<PrivateRoute>{route.element}</PrivateRoute>}
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";

/**
 * Returns true when the user has a valid auth session in localStorage.
 */
function isAuthenticated() {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) return false;
    const userDetails = localStorage.getItem("retailUserDetails");
    if (!userDetails) return false;
    if (userDetails === "null" || userDetails === "undefined") return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * EXACT public paths — only these specific paths are accessible without auth.
 * Do NOT use substring matching; every entry here grants access to that
 * exact pathname only.
 */
const PUBLIC_PATHS = new Set([
  "/", // LoginPage
  "/products/location", // LocationModalV2 — location selector
  "/dual-screen", // DualScreenDisplay — customer-facing display (separate window)
]);

export default function PrivateRoute({ children }) {
  const { pathname } = useLocation();

  if (PUBLIC_PATHS.has(pathname)) return children;

  if (!isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  return children;
}
